/** @format */

import React from "react";
import { Box, Typography, Button, IconButton, keyframes } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";

const HEALTH_LABEL = {
  ok: "Connected",
  down: "Offline",
  checking: "Checking…",
};

const HEALTH_COLOR = {
  ok: "#10b981",
  down: "#ef4444",
  checking: "#f59e0b",
};

const pulseAnim = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

const PilotHeader = ({
  merchantName,
  merchantId,
  turnCount,
  sessionId,
  healthStatus,
  onNewConversation,
  disableNew,
}) => {
  const dotColor = HEALTH_COLOR[healthStatus] || HEALTH_COLOR.checking;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: { xs: 1.5, md: 2.5 },
        py: { xs: 0.75, md: 1 },
        borderBottom: "1px solid #e2e8f0",
        backgroundColor: "#fff",
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          minWidth: 0,
          flex: 1,
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "8px",
            background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <BoltRoundedIcon sx={{ color: "#fff", fontSize: 18 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 700,
                color: "#0f172a",
                lineHeight: 1.2,
                letterSpacing: "-0.01em",
              }}
            >
              ProPilot
            </Typography>
            <Typography
              sx={{
                fontSize: 10,
                color: "#94a3b8",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                fontWeight: 600,
                display: { xs: "none", md: "inline" },
              }}
            >
              AI Shipment Assistant
            </Typography>
          </Box>
          {merchantName && (
            <Typography
              sx={{
                fontSize: 11,
                color: "#64748b",
                lineHeight: 1.3,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={merchantId ? `${merchantName} · ${merchantId}` : merchantName}
            >
              Merchant: <Box component="strong" sx={{ color: "#334155", fontWeight: 600 }}>{merchantName}</Box>
            </Typography>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: { xs: "none", md: "inline-flex" },
            alignItems: "center",
            gap: 0.5,
            fontSize: 11,
            color: "#64748b",
            px: 1,
            py: 0.5,
            backgroundColor: "#f8fafc",
            borderRadius: "999px",
            border: "1px solid #e2e8f0",
          }}
          title={sessionId ? `Session: ${sessionId}` : undefined}
        >
          <Box
            component="strong"
            sx={{ color: "#0f172a", fontWeight: 600 }}
          >
            {turnCount}
          </Box>
          {turnCount === 1 ? "turn" : "turns"}
        </Box>

        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            px: 1,
            py: 0.5,
            borderRadius: "999px",
            border: "1px solid #e2e8f0",
            backgroundColor: "#f8fafc",
          }}
          title={HEALTH_LABEL[healthStatus] || "Unknown"}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: dotColor,
              boxShadow: `0 0 0 3px ${dotColor}26`,
              ...(healthStatus === "checking"
                ? { animation: `${pulseAnim} 1.5s ease-in-out infinite` }
                : {}),
            }}
          />
          <Typography
            sx={{
              fontSize: 11,
              color: "#64748b",
              fontWeight: 500,
              display: { xs: "none", sm: "inline" },
            }}
          >
            {HEALTH_LABEL[healthStatus] || "Unknown"}
          </Typography>
        </Box>

        <Button
          size="small"
          disableElevation
          startIcon={<AddRoundedIcon />}
          onClick={onNewConversation}
          disabled={disableNew}
          sx={{
            display: { xs: "none", md: "inline-flex" },
            backgroundColor: "#6366f1",
            color: "#fff",
            textTransform: "none",
            fontWeight: 600,
            fontSize: 12,
            px: 1.5,
            py: 0.75,
            borderRadius: "8px",
            whiteSpace: "nowrap",
            "&:hover": { backgroundColor: "#4f46e5" },
            "&.Mui-disabled": {
              backgroundColor: "#e2e8f0",
              color: "#94a3b8",
            },
          }}
        >
          New Conversation
        </Button>
        {/* Icon-only variant keeps the header compact on phones. */}
        <IconButton
          size="small"
          onClick={onNewConversation}
          disabled={disableNew}
          aria-label="New conversation"
          title="New conversation"
          sx={{
            display: { xs: "inline-flex", md: "none" },
            width: 34,
            height: 34,
            borderRadius: "8px",
            backgroundColor: "#6366f1",
            color: "#fff",
            "&:hover": { backgroundColor: "#4f46e5" },
            "&.Mui-disabled": {
              backgroundColor: "#e2e8f0",
              color: "#94a3b8",
            },
          }}
        >
          <AddRoundedIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default PilotHeader;
